/**
 * What the chat bubbles tell the game report (P1.9-T23, ADR-068 decision 9), the switch a measurement turns them
 * off with, and the queue the page feeds them through: the page says every Local line the server's `said` message
 * carries, and the bubbles surface takes the waiting lines on its next frame. The queue lives here, beside the
 * counts, because the page receives lines whether or not the scene is running, and holds at most
 * `MAX_WAITING_LINES`, the oldest dropped first, so a scene that is not running never lets it grow.
 */

/**
 * A Local line as the server's `said` message carries it: the speaker's entity handle on the listener's map
 * server, the speaker's first name and family name (empty for a dev join), and the text, 1 to 200 code points as
 * the chat rule leaves it.
 */
export interface SpokenLine {
  readonly handle: number;
  readonly firstName: string;
  readonly familyName: string;
  readonly text: string;
}

/**
 * One bubble held, as the report lists it: the speaker's handle, the text drawn (the line after glyph mapping,
 * wrapping and cutting, its lines joined by a line feed), how many lines it holds, how many of the text's
 * characters the canvas's bitmap font holds no glyph for (Phaser draws those as nothing, so a bubble whose glyphs
 * are all in the atlas reads none), whether it is drawn, the frame's left, top, width and height in logical pixels at
 * the UI scale it was placed at, that scale, the tail's left, top, width and height in logical pixels and whether
 * it is drawn, and whether its display objects sit in the UI layer, which the UI camera draws over the whole world.
 */
export interface BubbleReading {
  readonly handle: number;
  readonly text: string;
  readonly lines: number;
  readonly undrawn: number;
  readonly shown: boolean;
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
  readonly scale: number;
  readonly tail: { readonly left: number; readonly top: number; readonly width: number; readonly height: number; readonly shown: boolean };
  readonly inUiLayer: boolean;
}

/**
 * The bubbles' report, switch and queue. `enabled` false hides every bubble, returns it to the pool and drops the
 * lines taken while it is off; `active`, `free`, `created` and `shown` are the pool's bubbles held, waiting, made
 * so far and drawn, after the last frame; `spoken` counts every line said; `list` reads every bubble held; `topOf`
 * says where a speaker's bubble drawn on the last frame stands, which an emoji over the speaker stands over (ADR-114
 * decision 5.2); `say` queues a line and `take` hands the waiting lines to a visitor in the order they were said,
 * emptying the queue.
 */
export interface BubbleReport {
  enabled: boolean;
  active: number;
  free: number;
  created: number;
  shown: number;
  spoken: number;
  list: () => readonly BubbleReading[];
  /**
   * The top of a speaker's bubble frame as drawn on the last frame, read on a frame without allocating.
   *
   * @param handle The speaker's handle.
   * @returns The frame's top in device pixels, or NaN when no bubble of the speaker's is drawn.
   */
  topOf: (handle: number) => number;
  /**
   * Queues a Local line for the bubbles' next frame.
   *
   * @param line The line.
   */
  say(line: SpokenLine): void;
  /**
   * Hands every waiting line to a visitor, oldest first, and empties the queue. A line said during the pass is
   * handed over in the same pass.
   *
   * @param visit Told each line.
   */
  take(visit: (line: SpokenLine) => void): void;
}

/**
 * The most lines the queue holds while nothing takes them. A speaker shows one bubble, so a frame needs no more
 * lines than there are speakers in view, and 64 lines is several seconds of a crowd's Local chat at the rate the
 * map server hands one listener (ADR-068 decision 4).
 */
export const MAX_WAITING_LINES = 64;

/**
 * A report with nothing drawn or said yet.
 *
 * @returns The report, its list empty until the bubbles are created.
 */
export function createBubbleReport(): BubbleReport {
  const waiting: (SpokenLine | undefined)[] = new Array<SpokenLine | undefined>(MAX_WAITING_LINES).fill(undefined);
  let head = 0;
  let size = 0;
  const report: BubbleReport = {
    enabled: true,
    active: 0,
    free: 0,
    created: 0,
    shown: 0,
    spoken: 0,
    /**
     * Lists no bubble until the bubbles surface is created and replaces this.
     *
     * @returns No readings.
     */
    list: () => [],
    /**
     * Places no bubble until the bubbles surface is created and replaces this.
     *
     * @returns NaN.
     */
    topOf: () => Number.NaN,
    /**
     * Queues a line, dropping the oldest waiting line when the queue is full.
     *
     * @param line The line.
     */
    say(line) {
      report.spoken += 1;
      if (size === MAX_WAITING_LINES) {
        waiting[head] = undefined;
        head = (head + 1) % MAX_WAITING_LINES;
        size -= 1;
      }
      waiting[(head + size) % MAX_WAITING_LINES] = line;
      size += 1;
    },
    /**
     * Hands every waiting line to a visitor, oldest first, releasing each slot before the visit.
     *
     * @param visit Told each line.
     */
    take(visit) {
      while (size > 0) {
        const line = waiting[head] as SpokenLine;
        waiting[head] = undefined;
        head = (head + 1) % MAX_WAITING_LINES;
        size -= 1;
        visit(line);
      }
    },
  };
  return report;
}
